<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  class: {
    type: String,
    default: '',
  },
  alt: {
    type: String,
    default: '',
  },
  width: {
    type: [Number, String],
    default: null,
  },
  height: {
    type: [Number, String],
    default: null,
  },
  loading: {
    type: String as () => 'lazy' | 'eager',
    default: 'lazy',
    validator: (value: string) => ['lazy', 'eager'].includes(value),
  },
  imgClass: {
    type: String,
    default: '',
  },
  sizes: {
    type: Object,
    default: () => ({ desktop: "1024x1024", tablet: "768x768", mobile: "512x512" })
  },
  sources: {
    type: Object,
    default: () => ({})
  },
  fetchpriority: {
    type: String as () => 'high' | 'low' | 'auto',
    default: 'auto',
  },
  mimeType: {
    type: String,
    default: '',
  }
});

// Reactive state to track failed sources
const failedSources = ref<Set<string>>(new Set());

// Define all screen types we want to support
const allScreenTypes = ['desktop', 'tablet', 'mobile'];

// Combine keys from both sizes and sources, ensuring we include all screen types
const screenKeys = computed(() => {
  const keysSet = new Set([
    ...allScreenTypes,
    ...Object.keys(props.sizes || {}),
    ...Object.keys(props.sources || {})
  ]);
  return Array.from(keysSet);
});

// Default sizes to use if a specific size is not provided
const defaultSizes = {
  desktop: "1024x1024",
  tablet: "768x768",
  mobile: "512x512"
};

// Get the appropriate size for a screen
const getSize = (screen: string) => {
  return props.sizes?.[screen] || defaultSizes[screen as keyof typeof defaultSizes] || "512x512"; // Fallback size
};

const getModifiedSrcSet = (url: string, screenSize: string) => {
  if (!url) return { webp: "", original: "" };

  const modifiedUrl = url.replace("/media/", "/thumbnail/");
  const match = modifiedUrl.match(/(.+)\.([a-zA-Z0-9]+)(\?.*)?$/);

  if (!match) return { webp: url, original: url };

  const [, base, ext, query] = match;
  const [width, height] = screenSize.split("x").map(Number);
  const size1x = `${width}x${height}`;
  const size2x = `${width * 2}x${height * 2}`;

  const webpSrcSet = `${base}_${size1x}.webp${query || ""}`;
  const originalSrcSet = `${base}_${size1x}.${ext}${query || ""}`;

  return { webp: webpSrcSet, original: originalSrcSet };
};

const getMediaQuery = (screen: string) => {
  const mediaQueries: Record<string, string> = {
    desktop: "(min-width: 992px)",
    tablet: "(min-width: 768px) and (max-width: 991px)",
    mobile: "(max-width: 767px)"
  };

  return mediaQueries[screen] || "";
};

const getMimeType = (screen: string) => {
  if (props.mimeType) {
    return props.mimeType;
  }
  return props.sources?.[screen]?.mimeType;
};

// Get source URL based on screen, with intelligent fallbacks
const getSourceUrl = (screen: string) => {
  // If we have a specific source for this screen, use it
  if (props.sources?.[screen]) {
    return props.sources[screen].url;
  }
  
  // Otherwise, apply intelligent fallbacks
  if (screen === 'tablet') {
    // For tablet, prefer desktop source over mobile if available
    return props.sources?.desktop?.url || props.sources?.mobile?.url || props.src;
  } else if (screen === 'mobile' && !props.sources?.mobile) {
    // For mobile without mobile source, use tablet or desktop
    return props.sources?.tablet?.url || props.sources?.desktop?.url || props.src;
  }
  
  // Default fallback
  return props.src;
};

// Check if a source URL has failed to load
const isSourceFailed = (url: string) => {
  return failedSources.value.has(url);
};

// Check if we should render a source for this screen
const shouldRenderSource = (screen: string) => {
  // We should render if there's a media query for this screen
  return !!getMediaQuery(screen);
};

// Check if we should render a specific source element
const shouldRenderSourceElement = (screen: string, format: 'webp' | 'original') => {
  if (!shouldRenderSource(screen)) return false;
  
  const sourceUrl = getSourceUrl(screen);
  const srcSet = getModifiedSrcSet(sourceUrl, getSize(screen));
  const url = format === 'webp' ? srcSet.webp : srcSet.original;
  // Don't render if the source has failed
  return !isSourceFailed(url) && url;
};

// Handle source load errors
const handleSourceError = (url: string) => {
  failedSources.value.add(url);
};

// Preload and validate sources on mount
// onMounted(() => {
//   // Preload sources to check for 404s
//   screenKeys.value.forEach((screen: string) => {
//     if (shouldRenderSource(screen)) {
//       const sourceUrl = getSourceUrl(screen);
//       const srcSet = getModifiedSrcSet(sourceUrl, getSize(screen));
      
//       // Check both webp and original formats
//       [srcSet.webp, srcSet.original].forEach(url => {
//         if (url && !isSourceFailed(url)) {
//           const img = new Image();
//           img.onerror = () => handleSourceError(url);
//           img.onload = () => {
//             // Source loaded successfully, no action needed
//           };
//           img.src = url;
//         }
//       });
//     }
//   });
// });
</script>

<template>
  <picture :class="class">
    <template v-for="screen in screenKeys" :key="screen">
      <template v-if="shouldRenderSource(screen) && getMimeType(screen) !== 'image/webp'">
        <source
          v-if="shouldRenderSourceElement(screen, 'webp')"
          :srcset="getModifiedSrcSet(getSourceUrl(screen), getSize(screen)).webp"
          type="image/webp"
          :media="getMediaQuery(screen)"
          @error="handleSourceError(getModifiedSrcSet(getSourceUrl(screen), getSize(screen)).webp)">
        <source
          v-if="shouldRenderSourceElement(screen, 'original')"
          :srcset="getModifiedSrcSet(getSourceUrl(screen), getSize(screen)).original"
          :media="getMediaQuery(screen)"
          :type="getMimeType(screen)"
          @error="handleSourceError(getModifiedSrcSet(getSourceUrl(screen), getSize(screen)).original)">
      </template>
    </template>
    
    <!-- Fallback Image -->
    <NuxtImg 
      :src="src" 
      :class="imgClass" 
      :loading="loading === 'lazy' ? 'lazy' : 'eager'" 
      :alt="alt" 
      :width="width" 
      :height="height"
      :type="getMimeType('desktop')"
      format="webp"
      :fetchpriority="fetchpriority"
      @error="handleSourceError(src)"/>
  </picture>
</template>